/**
 * Embed Authentication Context
 *
 * Provides authentication for TR.com embedded iframe.
 * Uses Partner Token authentication: ?token=ptr_xxx
 *
 * Partner tokens are secure as they are:
 * - Short-lived (8 hours)
 * - Can be tied to specific users
 * - Generated server-side by partner backends
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { logger } from '../utils/logger';
import { getBackendApiUrl } from '../config/backend';
import { dropStaffSiteTokenIf, inTirerackFrame, readStaffSiteToken } from '../utils/staffSiteToken';

interface EmbedAuthContextValue {
  /** The authentication credential (partner token) */
  credential: string | null;
  /** Whether authentication is valid */
  isValid: boolean;
  /** Whether we're still loading/validating */
  loading: boolean;
  /** Error message if authentication failed */
  error: string | null;
  /** Get the credential for use in API calls */
  getCredential: () => string | null;
}

const EmbedAuthContext = createContext<EmbedAuthContextValue | null>(null);

interface EmbedAuthProviderProps {
  children: ReactNode;
  /**
   * TRAC-757: When true, and no token is present in the URL, the provider
   * self-mints a public embed token (POST /api/partner/public-embed-token)
   * instead of erroring with "Missing authentication". Used by the unauthed
   * /gridlife page. Leave undefined for the standard /embed partner-token flow.
   */
  autoMint?: boolean;
}

const QA_TOKEN_STORAGE_KEY = 'traci_qa_session_token';

export const EmbedAuthProvider: React.FC<EmbedAuthProviderProps> = ({ children, autoMint = false }) => {
  const [credential, setCredential] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // TRACI-938: index.html moves ?token= out of the URL before the Adobe tag can
    // record it; the URL is the fallback for when that script did not run.
    const token = (window as Window & { __TRACI_URL_TOKEN__?: string }).__TRACI_URL_TOKEN__
      || params.get('token');
    // TRACI-519: a QA token handed over in ?qa_token= moves to this tab's
    // sessionStorage and out of the address bar (history, referrers) at once.
    // Reading storage first keeps a reload and React StrictMode's second
    // effect run signed in.
    const urlQaToken = params.get('qa_token');
    if (urlQaToken) {
      try { sessionStorage.setItem(QA_TOKEN_STORAGE_KEY, urlQaToken); } catch { /* storage blocked */ }
      params.delete('qa_token');
      const rest = params.toString();
      window.history.replaceState(
        window.history.state, '',
        window.location.pathname + (rest ? `?${rest}` : '') + window.location.hash,
      );
    }
    let qaToken = urlQaToken;
    try { qaToken = sessionStorage.getItem(QA_TOKEN_STORAGE_KEY) || urlQaToken; } catch { /* storage blocked */ }

    // QA session token — validate against backend
    if (qaToken) {
      logger.debug('QA session token detected, validating...');
      const backendUrl = (import.meta.env.VITE_BACKEND_API_URL as string) || 'http://localhost:8083';
      fetch(`${backendUrl}/api/qa/auth/verify-token`, {
        method: 'POST',
        headers: { Authorization: `Bearer qa_${qaToken}` },
      })
        .then(res => {
          if (res.ok) return res.json();
          throw new Error('Invalid QA token');
        })
        .then(data => {
          logger.debug('QA token validated:', data.email);
          // Use the qa_token as the credential — backend will accept it
          setCredential(`qa_${qaToken}`);
          setIsValid(true);
          setLoading(false);
        })
        .catch(err => {
          logger.error('QA token validation failed:', err);
          try { sessionStorage.removeItem(QA_TOKEN_STORAGE_KEY); } catch { /* storage blocked */ }
          setError('Invalid or expired QA session token.');
          setIsValid(false);
          setLoading(false);
        });
      return;
    }

    // CPOC-105: in the chat on www.tirerack.com, a staff member who turned on Connect
    // mode at /connect-poc chats with their own staff token instead of the site's
    // anonymous one. The server still decides who reaches Connect. It is checked once
    // first: a token the server refuses (revoked, forged) is dropped and this frame
    // reloads into the site's own sign-in, so the chat never dead-ends on it.
    if (inTirerackFrame()) {
      const staff = readStaffSiteToken();
      if (staff) {
        let cancelled = false;
        const use = (): void => {
          if (cancelled) return;
          logger.debug('Staff Connect mode: using the staff token saved by /connect-poc');
          setCredential(staff.token);
          setIsValid(true);
          setLoading(false);
        };
        fetch(`${getBackendApiUrl()}/api/partner/token/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: staff.token }),
        })
          .then((res) => (res.ok ? res.json() : null))
          .then((d) => {
            if (cancelled) return;
            if (d && d.valid === false) {
              logger.warn('Staff Connect mode token refused; back to the site sign-in');
              dropStaffSiteTokenIf(staff.token);
              window.location.reload();
              return;
            }
            use();
          })
          .catch(use); // could not check: the API is likely down for every token anyway
        return () => { cancelled = true; };
      }
    }

    // Partner token — existing flow
    if (token) {
      // Validate partner token format (should start with "ptr_")
      if (!token.startsWith('ptr_')) {
        setError('Invalid token format. Expected partner token (ptr_...).');
        setIsValid(false);
        setLoading(false);
        return;
      }

      if (token.length < 20) {
        setError('Invalid token format. Token too short.');
        setIsValid(false);
        setLoading(false);
        return;
      }

      logger.debug('Partner token detected, using token authentication');
      setCredential(token);
      setIsValid(true);
      setLoading(false);
      return;
    }

    // TRAC-757: Auto-mint path for the public /gridlife page — no token in the
    // URL, so request one from the unauthed public-embed-token endpoint.
    if (autoMint) {
      logger.debug('No URL token; auto-minting public embed token...');
      fetch(`${getBackendApiUrl()}/api/partner/public-embed-token`, { method: 'POST' })
        .then(res => {
          if (res.ok) return res.json();
          throw new Error(`Mint failed: ${res.status}`);
        })
        .then(data => {
          logger.debug('Public embed token minted');
          setCredential(data.token);
          setIsValid(true);
          setLoading(false);
        })
        .catch(err => {
          logger.error('Public embed token mint failed:', err);
          setError('Unable to start chat. Please try again.');
          setIsValid(false);
          setLoading(false);
        });
      return;
    }

    // TRAC-959: inside the page-level shell, the first page of a visit has no
    // token in the URL — the shell set our src immediately so this app's
    // download runs in parallel with its token mint, and hands the token down
    // over postMessage once minted. Ask for it (retrying, like the context
    // handshake — the parent handler is idempotent) instead of failing; the
    // skeleton covers the wait, and a 10s cap degrades to the error state.
    if (params.get('shell') === '1' && window.parent !== window) {
      logger.debug('No URL token in shell mode; requesting token from parent...');
      const env = import.meta.env.VITE_ENV || 'dev';
      const targetOrigin = env === 'prod' ? 'https://www.tirerack.com' : '*';
      let settled = false;
      const cleanup = (): void => {
        window.removeEventListener('message', onMsg);
        clearInterval(retryId);
        clearTimeout(timeoutId);
      };
      const onMsg = (e: MessageEvent): void => {
        const d = e.data || {};
        if (
          e.source === window.parent &&
          d.type === 'TRACI_TOKEN' &&
          typeof d.token === 'string' &&
          d.token.startsWith('ptr_')
        ) {
          settled = true;
          cleanup();
          logger.debug('Token received from parent shell');
          setCredential(d.token);
          setIsValid(true);
          setLoading(false);
        }
      };
      window.addEventListener('message', onMsg);
      const ask = (): void => {
        window.parent.postMessage({ type: 'TRACI_REQUEST_TOKEN', source: 'AI-Assistant' }, targetOrigin);
      };
      ask();
      const retryId = setInterval(() => { if (!settled) ask(); }, 400);
      const timeoutId = setTimeout(() => {
        if (settled) return;
        cleanup();
        logger.error('Parent shell never supplied a token (10s)');
        setError('Missing authentication. Please provide ?token= parameter.');
        setIsValid(false);
        setLoading(false);
      }, 10000);
      return cleanup;
    }

    // No authentication provided
    setError('Missing authentication. Please provide ?token= parameter.');
    setIsValid(false);
    setLoading(false);
  }, [autoMint]);

  const getCredential = (): string | null => {
    return credential;
  };

  const value: EmbedAuthContextValue = {
    credential,
    isValid,
    loading,
    error,
    getCredential,
  };

  return (
    <EmbedAuthContext.Provider value={value}>
      {children}
    </EmbedAuthContext.Provider>
  );
};

export const useEmbedAuth = (): EmbedAuthContextValue => {
  const context = useContext(EmbedAuthContext);
  if (!context) {
    throw new Error('useEmbedAuth must be used within an EmbedAuthProvider');
  }
  return context;
};

export default EmbedAuthContext;
